import { useEffect, useState } from "react";
import { Loader2, ExternalLink, FileDown } from "lucide-react";
import api from "@/lib/api";

const STATUS = {
  paid: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  open: { label: "Unpaid", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  overdue: { label: "Overdue", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  void: { label: "Void", cls: "bg-gray-700/40 text-gray-400 border-gray-700" },
  uncollectible: { label: "Uncollectible", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
  draft: { label: "Draft", cls: "bg-gray-700/40 text-gray-400 border-gray-700" },
};

const fmt = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const InvoiceHistory = ({ userId, self = false }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(self ? "/billing/invoices" : `/admin/users/${userId}/invoices`).then((r) => setRows(r.data.invoices))
      .catch((e) => { setError(e.response?.data?.detail || "Couldn't load invoices"); setRows([]); });
  }, [userId, self]);

  if (rows === null) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>;
  if (error) return <p data-testid="invoices-error" className="px-5 py-8 text-sm text-red-400 text-center">{error}</p>;
  if (!rows.length) return <p data-testid="invoices-empty" className="px-5 py-8 text-sm text-gray-500 text-center">No invoices yet.</p>;

  const unpaid = rows.filter((r) => r.status === "open" || r.status === "overdue").reduce((s, r) => s + r.amount_due - r.amount_paid, 0);
  return (
    <div data-testid="invoice-history">
      {unpaid > 0 && <div data-testid="invoices-unpaid-total" className="px-5 py-2.5 text-xs text-amber-300 bg-amber-500/10 border-b border-gray-800">Outstanding balance: <span className="font-semibold">{money(unpaid)}</span></div>}
      <div className="divide-y divide-gray-800/60 max-h-[420px] overflow-y-auto">
        {rows.map((r) => {
          const st = STATUS[r.status] || STATUS.draft;
          return (
            <div key={r.id} data-testid={`invoice-row-${r.id}`} className="px-5 py-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-white font-semibold flex items-center gap-3">
                  {money(r.status === "paid" ? r.amount_paid : r.amount_due)}
                  {self && r.url && (r.status === "open" || r.status === "overdue") && <a data-testid={`invoice-pay-${r.id}`} href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center px-2.5 py-1 rounded-lg gold-gradient text-black text-[11px] font-semibold hover:brightness-110">Pay now</a>}
                </p>
                <span data-testid={`invoice-status-${r.id}`} className={"px-2 py-0.5 rounded-full text-[11px] font-semibold border " + st.cls}>{st.label}</span>
              </div>
              <p className="mt-0.5 text-xs text-gray-400 truncate">{r.description || (r.manual ? "Manual invoice" : "Subscription")}</p>
              <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500">
                <span>{r.number || "—"} · {fmt(r.created)}{r.status === "paid" && r.paid_at ? ` · paid ${fmt(r.paid_at)}` : ""}</span>
                <span className="flex items-center gap-2">
                  {r.url && <a data-testid={`invoice-view-${r.id}`} href={r.url} target="_blank" rel="noreferrer" className="hover:text-amber-300" title="Open invoice"><ExternalLink className="h-3.5 w-3.5" /></a>}
                  {r.pdf && <a data-testid={`invoice-pdf-${r.id}`} href={r.pdf} target="_blank" rel="noreferrer" className="hover:text-amber-300" title="Download PDF"><FileDown className="h-3.5 w-3.5" /></a>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
